/**
 * ProductForm Component
 *
 * This component handles creating new products with essential fields only.
 * The form uses a centralized schema (productEditSchema) and default values function
 * (getDefaultProductValues) from the src/schemas/product.ts file.
 *
 * Key features:
 * - Form for create operations only (category and description set later in product detail page)
 * - Validation using Zod schema
 * - Essential fields: name, sku, status, and family
 * - Streamlined UX for fast product creation
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  productEditSchema,
  getDefaultProductValues,
  ProductFormValues,
  createDynamicProductSchema,
} from '@/schemas/product';

// UI components
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { SplashLoader } from '@/components/SplashLoader';

// Services
import { productService, Product } from '@/services/productService';

// Translation hook
import { useFormsTranslations } from '@/hooks/useFormsTranslations';

import { useFamilies } from '@/api/familyApi';
import { Family } from '@/types/family';
import { logError, logWarn, logDebug } from '@/utils/errorHandler';

export function ProductForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const forms = useFormsTranslations();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [validationSchema, setValidationSchema] = useState<any>(productEditSchema);

  // Fetch families
  const { data: families, isLoading: isFamiliesLoading } = useFamilies();

  // Initialize dynamic validation schema
  useEffect(() => {
    const initializeValidation = async () => {
      try {
        // Use enhanced validation for create context
        const dynamicSchema = await createDynamicProductSchema('create', true);
        setValidationSchema(dynamicSchema);
      } catch (error) {
        logWarn(
          'Failed to load dynamic validation schema, using legacy schema',
          {
            component: 'ProductForm',
            action: 'initializeValidation',
            metadata: { error },
          },
          false
        );
        // Keep using productEditSchema as fallback
      }
    };

    initializeValidation();
  }, []);

  // Form definition using react-hook-form with dynamic schema
  const form = useForm<ProductFormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: getDefaultProductValues(undefined),
    mode: 'onSubmit', // Set submission mode
  });

  // Watch family field value for state sync
  const watchedFamilyId = form.watch('family');

  // Sync selectedFamily state with form value
  useEffect(() => {
    if (watchedFamilyId && families) {
      const family = families.find(f => f.id === watchedFamilyId);
      setSelectedFamily(family || null);
    } else {
      setSelectedFamily(null);
    }
  }, [watchedFamilyId, families]);

  // Handle form submission
  const onSubmit = (values: ProductFormValues) => {
    setIsLoading(true);

    // Make a copy of the payload to modify it
    const payload: any = {
      ...values,
    };

    // Category will be set later in the product detail page

    // For create mode: completely remove tags to avoid the API error
    delete payload.tags;

    logDebug('Form submission debug information', {
      formValues: values,
      payload,
      familyInFormValues: values.family,
      familyInPayload: payload.family,
      selectedFamilyState: selectedFamily,
      watchedFamilyId: form.watch('family'),
      formGetValues: form.getValues(),
    });

    logDebug('Creating product with family', { family: values.family });

    // Additional debugging: Check if family field is being filtered out
    if (values.family !== undefined && values.family !== null) {
      logDebug('Family field has value', {
        familyValue: values.family,
        familyType: typeof values.family,
        isInPayload: 'family' in payload,
        payloadFamily: payload.family,
      });
    } else {
      logDebug('Family field is null/undefined', {
        familyValue: values.family,
        selectedFamilyState: selectedFamily,
        watchedValue: form.watch('family'),
      });
    }

    // Create mode - first create the product without the image
    productService
      .createProduct(payload as any)
      .then(async (result: Product) => {
        const newProductId = result.id;

        if (!newProductId) {
          throw new Error('Created product but no ID was returned');
        }

        toast({ title: forms.product.notifications.createSuccess });

        // Invalidate product queries to refresh the UI
        queryClient.invalidateQueries({
          queryKey: ['product', newProductId],
        });
        queryClient.invalidateQueries({
          queryKey: ['assets', newProductId],
        });

        // Navigate to product detail page with new product indicator
        navigate(`/app/products/${newProductId}?new=true`, { replace: true });
      })
      .catch((error: unknown) => {
        logError(
          'Failed to create product',
          {
            component: 'ProductForm',
            action: 'onSubmit',
            metadata: { error, payload },
          },
          { showToast: false, userFacing: false }
        );
        const errorMessage =
          error instanceof Error ? error.message : forms.product.notifications.createFailed;
        toast({ title: errorMessage, variant: 'destructive' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isFamiliesLoading) {
    return <SplashLoader />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold">{forms.product.form.title}</h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 pb-10">
          <div className="space-y-6">
            <div>
              <h2 className="mb-4 text-lg font-medium">{forms.product.form.tabs.basicInfo}</h2>

              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="name" className="block text-sm font-medium">
                    {forms.product.labels.name} *
                  </label>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input {...field} placeholder={forms.product.placeholders.name} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="sku" className="block text-sm font-medium">
                    {forms.product.labels.sku} *
                  </label>
                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input {...field} placeholder={forms.product.placeholders.sku} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="status" className="block text-sm font-medium">
                    {forms.product.labels.status} *
                  </label>
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          onValueChange={value => field.onChange(value === 'true')}
                          value={field.value ? 'true' : 'false'}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={forms.product.placeholders.selectStatus} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="true">{forms.product.status.active}</SelectItem>
                            <SelectItem value="false">{forms.product.status.inactive}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Family select field */}
              <FormField
                control={form.control}
                name="family"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{forms.product.form.familyInfo.label}</FormLabel>
                    <Select
                      onValueChange={value => {
                        // Handle the form field update properly
                        const familyValue = value === 'none' || value === '' ? null : Number(value);
                        field.onChange(familyValue);

                        // Update selectedFamily state for UI display
                        const family = families?.find(f => f.id === Number(value)) || null;
                        setSelectedFamily(family);

                        logDebug('Selected family from dropdown', {
                          originalValue: value,
                          processedValue: familyValue,
                          family,
                        });
                      }}
                      value={field.value ? String(field.value) : 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={forms.product.placeholders.selectFamily} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">{forms.product.form.familyInfo.none}</SelectItem>
                        {families?.map(family => (
                          <SelectItem key={family.id} value={family.id.toString()}>
                            {family.label} ({family.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-gray-500">
                      {selectedFamily ? (
                        <>
                          <span className="block font-medium text-blue-600">
                            {forms.product.form.familyInfo.helpSelected}
                          </span>
                          {selectedFamily?.attribute_groups &&
                          selectedFamily.attribute_groups.length > 0 ? (
                            <span className="mt-1 block">
                              {forms.product.form.familyInfo.helpGroupCount(
                                selectedFamily.attribute_groups.length,
                                selectedFamily.attribute_groups.filter(g => g.required).length
                              )}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="block text-amber-600">
                          {forms.product.form.familyInfo.helpNoFamily}
                        </span>
                      )}
                      <span className="mt-2 block font-medium text-blue-600">
                        {forms.product.form.familyInfo.helpAdditional}
                      </span>
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/products')}
              disabled={isLoading}
            >
              {forms.product.buttons.cancel}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Spinner className="mr-2 size-4" /> : forms.product.buttons.create}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
